import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthImageSide from "./AuthImageSide";
import logo from "../../assets/images/isotipo-invecasa.svg";
import "./AuthPage.css";
import { signup } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";

import {
  hasDifferentLoggedUser,
  saveAuthMeta,
} from "../../context/AuthSync";

export default function SignupPage() {
  const { login: authLogin, authenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const passwordMismatch =
    form.password && form.confirm && form.password !== form.confirm;

  useEffect(() => {
    if (authenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [authenticated, navigate]);

  function validatePassword(password) {
    const errors = [];
    if (!/[A-Z]/.test(password)) errors.push("Password must include an uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Password must include a lowercase letter");
    if (!/\d/.test(password)) errors.push("Password must include a number");
    if (!/[@$!%*?&._-]/.test(password)) errors.push("Password must include a special character (@$!%*?&._-)");
    if (password.length < 8) errors.push("Password must be at least 8 characters long");
    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage("");
    if (name === "password") {
      setPasswordErrors(validatePassword(value));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.name || !form.email || !form.password || !form.confirm) {
      setErrorMessage("Please complete all fields.");
      return;
    }

    if (form.password !== form.confirm) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    const errors = validatePassword(form.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      setErrorMessage("Password does not meet the requirements.");
      return;
    }

    if (hasDifferentLoggedUser(form.email)) {
      setErrorMessage("");
      navigate("/dashboard", { replace: true });
      return;
    }

    setLoading(true);
    try {
      const response = await signup({
        displayName: form.name,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirm,
        isGoogle: false,
      });

      authLogin(response.token);

      saveAuthMeta({ email: form.email });

      navigate("/dashboard");
    } catch (err) {
      console.error("Signup error:", err);
      const errorCode = err?.code;
      const defaultMessage = "An unexpected error occurred. Please try again later.";

      switch (errorCode) {
        case "SIU001":
          setErrorMessage("Please complete all fields.");
          break;
        case "SIU002":
          setErrorMessage("This email is already registered.");
          break;
        case "SIU003":
          setErrorMessage("Passwords do not match.");
          break;
        case "SIU004":
          setErrorMessage("Password does not meet the minimum requirements.");
          break;
        default:
          setErrorMessage(defaultMessage);
      }
    } finally {
      setLoading(false);
    }
  }

  function handleGoogleSignup() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_URL}/google/callback`;
    const scope = "openid email profile";
    const responseType = "code";

    const googleUrl =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?response_type=${responseType}` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&access_type=offline` +
      `&prompt=consent`;

    window.location.href = googleUrl;
  }

  return (
    <div className="authpage">
      <AuthImageSide />
      <div className="authpage__form-container">
        <div className="authpage__card">
          <div className="authpage__logo">
            <img src={logo} alt="Logo" />
          </div>
          <h2 className="authpage__title">Create your account</h2>
          <p className="authpage__subtitle">
            Join INVESCASA dashboards, all the data you need to make an informed decision
          </p>
          <form onSubmit={handleSubmit}>
            <div className="authpage__input-warning-wrap">
              <input
                type="text"
                className={`authpage__input ${form.name ? "authpage__input--filled" : ""}`}
                placeholder="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                autoComplete="name"
              />
            </div>
            <div className="authpage__input-warning-wrap">
              <input
                type="email"
                className={`authpage__input ${form.email ? "authpage__input--filled" : ""}`}
                placeholder="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
                onInvalid={(e) => e.target.setCustomValidity("Please enter a valid email address")}
                onInput={(e) => e.target.setCustomValidity("")}
              />
            </div>
            <div className="authpage__input-warning-wrap authpage__input-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className={`authpage__input ${form.password ? "authpage__input--filled" : ""}`}
                placeholder="Password"
                name="password"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {form.password && (
                <button
                  type="button"
                  className="authpage__input-visibility-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {/* Eye icon */}
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <line x1="5" y1="15" x2="15" y2="5" stroke="#8493ad" strokeWidth="1.5" />
                    </svg>
                  )}
                </button>
              )}
            </div>
            <div className="authpage__input-warning-wrap authpage__input-password-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                className={`authpage__input ${passwordMismatch ? "authpage__input--warning" : ""} ${form.confirm ? "authpage__input--filled" : ""}`}
                placeholder="Confirm password"
                name="confirm"
                value={form.confirm}
                onChange={handleChange}
                autoComplete="new-password"
                style={{ paddingRight: passwordMismatch ? 80 : 48 }}
              />
              {form.confirm && (
                <button
                  type="button"
                  className="authpage__input-visibility-btn"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                  style={{ right: 16 }}
                >
                  {/* Eye icon */}
                  {showConfirm ? (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20">
                      <path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5" />
                      <line x1="5" y1="15" x2="15" y2="5" stroke="#8493ad" strokeWidth="1.5" />
                    </svg>
                  )}
                </button>
              )}
            </div>

            {passwordErrors.length > 0 && (
              <ul style={{ color: "#bdbdbd", fontSize: "0.85rem", marginTop: "8px", marginBottom: "8px" }}>
                {passwordErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}

            {errorMessage && (
              <p style={{ color: "#bdbdbd", fontSize: "0.9rem", marginBottom: "12px", marginTop: "8px" }}>
                {errorMessage}
              </p>
            )}

            <button className="authpage__login-btn" type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create account"}
            </button>

            <button className="authpage__google-btn" type="button" onClick={handleGoogleSignup}>
              <img
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                alt="Google"
                className="authpage__google-logo"
              />
              Sign up with Google
            </button>
          </form>

          <div className="authpage__signup">
            Already have an account?
            <Link to="/login" className="authpage__signup-link"> Log in </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
